import { useEffect, useState } from 'react';
import { fetchExportPresets } from '@/features/exports/services/exportsApi';
import type { ExportPresetList } from '@/features/exports/types';

/**
 * Whether social exports are offered for a video, and with which presets.
 *
 * Asked once per video, and only when `enabled` (a completed video with a file). The
 * backend answers 404 while the feature is off, so a 404 and any other failure mean the
 * same thing here: no presets, nothing to show. There is deliberately no error state —
 * a feature that is off, or briefly unreachable, must leave the page exactly as it was.
 */
export function useExportPresets(videoId: string | undefined, enabled: boolean): ExportPresetList | null {
  const [result, setResult] = useState<{ videoId: string; presets: ExportPresetList } | null>(null);

  useEffect(() => {
    if (!videoId || !enabled) return;
    let active = true;
    fetchExportPresets(videoId)
      .then((list) => {
        if (active && list && Array.isArray(list.presets) && list.presets.length > 0) {
          setResult({ videoId, presets: list });
        }
      })
      .catch(() => {
        /* off, unreachable or not allowed: all mean "do not offer it" */
      });
    return () => {
      active = false;
    };
  }, [videoId, enabled]);

  return enabled && result && result.videoId === videoId ? result.presets : null;
}
